<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetMeterGeneralData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    meter: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const meterTypes = ref(null);
  const meterGroups = ref(null);
  const units = ref(['n/a', 'kWh', 'MWh', 'm3', 'GJ']);
  const ux = ref([]);
  const b_1548630417156826_dataSource = computed(() => ({
    name: 'suggest_meter',
  }));
  const b_7072234632991584_parameters = computed(() => ({
    type: 'property',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    meterTypes: meterTypes.value,
    meterGroups: meterGroups.value,
    units: units.value,
    ux: ux.value,
    b_1548630417156826_dataSource: b_1548630417156826_dataSource.value,
    b_7072234632991584_parameters: b_7072234632991584_parameters.value,
  }));

  const $$load_meterTypes = () => {
    return $fn.value.getEnumValuesFromSchema('MeterTypes');
  };
  const $$load_meterGroups = () => {
    return $fn.value
      .fetch('list_group')
      .then((data) => data.data.map((p) => p.name));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'meterTypes' },
      { name: 'meterGroups' },
      { name: 'units' },
      { name: 'ux' },
      { name: 'b_1548630417156826_dataSource', nodeId: '1548630417156826' },
      { name: 'b_7072234632991584_parameters', nodeId: '7072234632991584' },
    ],
    loaders: { meterTypes: $$load_meterTypes, meterGroups: $$load_meterGroups },
    ux: {},
    dataRefs: {
      labelAlign,
      meterTypes,
      meterGroups,
      units,
      ux,
      b_1548630417156826_dataSource,
      b_7072234632991584_parameters,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const act_7315092382398562_reloadComponentData_8quq214h1 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_igql3fjni =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_8quq214h1,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_igql3fjni],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_igql3fjni,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_igql3fjni = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_igql3fjni.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend class="text-muted">
      {{ pgettext('Meter', 'General') }}
    </legend>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="meter_type"
      :label="pgettext('Meter', 'Meter type')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormNativeSelect
        name="meter_type"
        v-model="meter.meter_type"
        :options="meterTypes"
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="parent"
      :label="pgettext('Meter', 'Main meter')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormSearchableSelect
        name="parent"
        v-model="meter.parent"
        clearable
        :dataSource="b_1548630417156826_dataSource"
        :initialOptions="meter.main_meter ? [meter.main_meter] : []"
        valueField="id"
        textField="serial_number"
        use-option-template-for-selected-option
        optionTemplate='<UcSuggestMeterOption :option="option"></UcSuggestMeterOption>'
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="meter_unit"
      :label="pgettext('Meter', 'Unit and multiplier')"
      :labelAlign="labelAlign"
      contentClass="row"
      required
      :helpTooltip="
        pgettext(
          'Meter',
          'The multiplier converts the readings into the given units.'
        )
      "
    >
      <div class="col-4">
        <ItpFormNativeSelect
          name="unit"
          v-model="meter.unit"
          :options="units"
          :placeholder="pgettext('Meter', 'Unit')"
        >
        </ItpFormNativeSelect>
      </div>
      <div class="col-4">
        <ItpFormInput
          type="number"
          name="multiplier"
          v-model="meter.multiplier"
          :placeholder="pgettext('Meter', 'Multiplier')"
          :title="pgettext('Meter', 'Multiplier')"
        >
        </ItpFormInput>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="model_name"
      :label="pgettext('Meter', 'Model name')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="model_name" v-model="meter.model_name">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="smart_device"
      :label="pgettext('Meter', 'Smart Device')"
      :labelAlign="labelAlign"
    >
      <ItpFormCheckbox name="smart_device" v-model="meter.smart_device">
      </ItpFormCheckbox>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="groups"
      :label="pgettext('Meter', 'Groups')"
      :labelAlign="labelAlign"
    >
      <ItpFormSearchableSelect
        name="groups"
        v-model="meter.groups"
        multiple
        :options="meterGroups"
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('Meter', 'Site')"
      :labelAlign="labelAlign"
    >
      <UcSiteSelector
        :item="meter"
        path="site"
        :parameters="b_7072234632991584_parameters"
      >
      </UcSiteSelector>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('Meter', 'Description')"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea name="description" v-model="meter.description">
      </ItpFormTextarea>
    </ItpFormGroup>
    <hr />
    <legend class="text-muted">
      {{ pgettext('Meter', 'Numbers') }}
    </legend>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="serial_number"
      :label="pgettext('Meter', 'Serial number')"
      :labelAlign="labelAlign"
      :helpTooltip="pgettext('Meter', 'The device\'s unique serial number.')"
      required
    >
      <ItpFormInput name="serial_number" v-model="meter.serial_number">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="deveui"
      :label="pgettext('Meter', 'DevEUI')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'Meter',
          '64-bit globally-unique Extended Unique Identifier (EUI-64) assigned by the manufacturer, or the owner, of the end-device.'
        )
      "
    >
      <ItpFormInput name="deveui" v-model="meter.deveui"> </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="tag_id"
      :label="pgettext('Meter', 'Tag ID')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'Meter',
          'Locally-unique tag ID to be used within the company.'
        )
      "
    >
      <ItpFormInput name="tag_id" v-model="meter.tag_id"> </ItpFormInput>
    </ItpFormGroup>
    <hr />
    <FieldsetMeterValidityData :meter="meter" :labelAlign="labelAlign">
    </FieldsetMeterValidityData>
  </ItpUsercontrol>
</template>
